import { formatHumanReadableRule } from "@domain/monitors"
import { type AlertIncidentCondition, type IncidentNotificationKey, SEVERITY_COLOR } from "@domain/shared"
import type { SignalPriority } from "@domain/signals"
import type { ActionsBlock, HeaderBlock, KnownBlock, SectionBlock } from "@slack/web-api"

export const header = (text: string): HeaderBlock => ({
  type: "header",
  text: { type: "plain_text", text: text.slice(0, 150), emoji: true },
})

/**
 * Slack mrkdwn reserves `&`, `<` and `>`; an unescaped `>` inside a `<url|label>`
 * closes the link early. Apply to any value a user can name.
 */
export const escapeMrkdwn = (text: string): string =>
  text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;")

export const sectionMarkdown = (text: string): SectionBlock => ({
  type: "section",
  text: { type: "mrkdwn", text },
})

export const actionsLink = (label: string, url: string): ActionsBlock => ({
  type: "actions",
  elements: [
    {
      type: "button",
      text: { type: "plain_text", text: label.slice(0, 75), emoji: true },
      url,
      action_id: "open_in_latitude",
    },
  ],
})

export const contextLine = (text: string): KnownBlock => ({
  type: "context",
  elements: [{ type: "mrkdwn", text }],
})

/** Emoji per manual issue priority for the context-line suffix. */
const PRIORITY_EMOJI: Record<SignalPriority, string> = {
  urgent: "\u{1F534}", // red circle
  high: "\u{1F7E0}", // orange circle
  medium: "\u{1F7E1}", // yellow circle
  low: "\u{1F7E2}", // green circle
}

/** "🟠 High" — emoji + capitalized priority, shared by every renderer that shows one. */
export const priorityLabel = (priority: SignalPriority): string =>
  `${PRIORITY_EMOJI[priority]} ${priority.charAt(0).toUpperCase()}${priority.slice(1)}`

/**
 * " · 🟠 High · Assigned to Anna" suffix for the incident context line.
 * Empty string when the payload carries no triage snapshot (legacy rows,
 * monitor sources) so the line renders exactly as before.
 */
export const triageContextSuffix = (input: {
  readonly priority: SignalPriority | null | undefined
  readonly assigneeName: string | null | undefined
}): string => {
  const parts: string[] = []
  if (input.priority) {
    parts.push(priorityLabel(input.priority))
  }
  if (input.assigneeName) {
    parts.push(`Assigned to ${input.assigneeName}`)
  }
  return parts.length > 0 ? ` \u{B7} ${parts.join(" \u{B7} ")}` : ""
}

export const projectOrOrgContext = (
  organization: { readonly name: string },
  project: { readonly name: string } | null,
): string => (project ? `Project *${project.name}* · ${organization.name}` : `Org *${organization.name}*`)

/** `/projects/<slug>/monitors?monitorSlug=<slug>` deep link; `null` when either slug is missing. */
export const monitorDeepLink = (input: {
  readonly webAppUrl: string
  readonly projectSlug: string | undefined
  readonly monitorSlug: string | undefined
}): string | null => {
  if (!input.projectSlug || !input.monitorSlug) return null
  return `${input.webAppUrl.replace(/\/$/, "")}/projects/${input.projectSlug}/monitors?monitorSlug=${encodeURIComponent(input.monitorSlug)}`
}

/** "Created by monitor X" context line (+ humanised rule); `[]` on legacy incidents. Deep-links when the slug resolves, else bold text. */
export const monitorAttributionBlocks = (input: {
  readonly webAppUrl: string
  readonly projectSlug: string | undefined
  readonly monitorName: string | undefined
  readonly monitorSlug: string | undefined
  readonly incidentKind: IncidentNotificationKey
  readonly condition: AlertIncidentCondition | null | undefined
}): KnownBlock[] => {
  if (!input.monitorName) return []
  const url = monitorDeepLink(input)
  const name = url ? `<${url}|${input.monitorName}>` : `*${input.monitorName}*`
  const blocks: KnownBlock[] = [contextLine(`Created by monitor ${name}`)]
  if (input.condition) {
    const trigger = input.incidentKind.replace("monitor.", "")
    if (trigger === "match" || trigger === "threshold" || trigger === "escalating") {
      blocks.push(contextLine(formatHumanReadableRule({ trigger, condition: input.condition })))
    }
  }
  return blocks
}

/**
 * Returns a Slack `image` block that renders the incident trend chart.
 * Slack fetches the URL async after posting, so the notification row
 * only needs to exist by the time Slack processes the image — which is
 * well after the create-notification workers have committed.
 *
 * Returns `null` when `notificationId` is absent (kinds that don't
 * write a bell-feed row, or a missing context value).
 */
export const trendChartBlock = (notificationId: string | null, webAppUrl: string): KnownBlock | null => {
  if (!notificationId) return null
  const base = webAppUrl.replace(/\/$/, "")
  const url = `${base}/api/notifications/${encodeURIComponent(notificationId)}/incident-trend.png`
  return {
    type: "image",
    image_url: url,
    alt_text: "Incident trend chart",
  } as KnownBlock
}

/**
 * Color constants for attachment bars.
 *
 * Severity tiers come from the shared {@link SEVERITY_COLOR} palette so the
 * bar matches the web charts and email badges. `resolved` is always green
 * (incident is over regardless of severity). `wrapped` is Claude Code orange.
 */
export const COLORS = {
  // Severity tiers — used by incident renderers
  ...SEVERITY_COLOR,
  critical: "#C0392B", // dark red (reserved for future severity tier)
  // Lifecycle overrides
  resolved: "#27AE60", // green — incident recovered regardless of severity
  wrapped: "#E8700A", // orange — Claude Code brand
  announcement: "#2F80ED",
} as const

/**
 * Returns the severity bar color. Falls back to red for unknown values
 * so there's always a visible bar.
 */
export const severityColor = (severity: string): string => (COLORS as Record<string, string>)[severity] ?? COLORS.high
